import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiWithAuth from '../../services/apiWithAuth';
import { usePermissions } from '../../hooks/usePermissions';

// --- SVG Icon Components ---

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);

const WalletIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
);

const FilterIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>
);

const EyeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
);

const StorageIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
    </svg>
);

const PLAN_TYPE_LABELS = {
    F_MONTHLY: 'FREE MONTHLY',
    B_MONTHLY: 'BRONZE MONTHLY',
    S_MONTHLY: 'SILVER MONTHLY',
    G_MONTHLY: 'GOLD MONTHLY',
    F_YEARLY: 'FREE YEARLY',
    B_YEARLY: 'BRONZE YEARLY',
    S_YEARLY: 'SILVER YEARLY',
    G_YEARLY: 'GOLD YEARLY'
};

const resolvePlanDailyRate = (plan) => {
    if (!plan) return 0;
    if (plan.dailyDeductionRate != null && plan.dailyDeductionRate >= 0) {
        return Number(plan.dailyDeductionRate);
    }
    const days = plan.period === 'yearly' ? 365 : 30;
    return parseFloat((plan.price / days).toFixed(2));
};

const ToggleIcon = ({ isActive }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        {isActive ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />

        ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />

        )}
    </svg>
);
const AdminWalletDashboard = () => {
    const permissions = usePermissions(['Wallet', 'finance/wallet transactions']);
    // --- STATE ---
    const [wallets, setWallets] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalWallets: 0,
        limit: 20, // Default limit
        hasNextPage: false,
        hasPrevPage: false
    });
    // <-- ADDED: State for rows per page dropdown -->
    const [limit, setLimit] = useState(20);
    const [showForm, setShowForm] = useState(false);
    const [selectedWallet, setSelectedWallet] = useState(null);
    const [searchFilters, setSearchFilters] = useState({
        name: '',
        phone: '',
        shopName: ''
    });
    const [showTransactions, setShowTransactions] = useState(false);
    const [selectedTransactions, setSelectedTransactions] = useState(null);
    const [showStorage, setShowStorage] = useState(false);
    const [selectedStorage, setSelectedStorage] = useState(null);
    const [storageData, setStorageData] = useState({});
    const [formData, setFormData] = useState({
        amount: '',
        description: '',
        planType: ''
    });
    const [creditPlans, setCreditPlans] = useState([]);
    const [loadingCreditPlans, setLoadingCreditPlans] = useState(false);
    const [togglingWallets, setTogglingWallets] = useState(new Set()); // Track which wallets are being toggled

    // Filters
    const [filters, setFilters] = useState({
        isActive: '',
        minBalance: '',
        planType: ''
    });
    const [showFilters, setShowFilters] = useState(false);

    // Loading and error states
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null); // For success messages

    // Clears both error and success messages
    const clearMessages = useCallback(() => {
        setError(null);
        setSuccess(null);
    }, []);

    // --- WALLET TOGGLE HANDLER ---
    const handleToggleWalletActive = async (userId, currentStatus) => {
        setTogglingWallets(prev => new Set([...prev, userId]));
        clearMessages();

        try {
            const response = await apiWithAuth.patch(`/wallet/admin/toggle-status`, {
                userId: userId,
                isActive: !currentStatus
            });

            if (response.data.success) {
                // Refresh wallets data to reflect the change
                await fetchWallets(pagination.currentPage);
                setSuccess(`Wallet ${!currentStatus ? 'activated' : 'deactivated'} successfully.`);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to update wallet status';
            setError(errorMessage);
        } finally {
            setTogglingWallets(prev => {
                const newSet = new Set(prev);
                newSet.delete(userId);
                return newSet;
            });
        }
    };

    // --- FETCH STORAGE DATA ---
    const fetchStorageData = useCallback(async () => {
        try {
            // Fetch all shops storage data using the admin panel API
            const response = await apiWithAuth.get('/shops/storage', {
                params: {
                    limit: 1000 // Get all shops, adjust if needed
                }
            });

            if (response.data.success && response.data.data.shopsData) {
                const storageMap = {};

                // Group shops by user ID
                response.data.data.shopsData.forEach(shop => {
                    const userId = shop.userId;
                    
                    if (userId) {
                        if (!storageMap[userId]) {
                            storageMap[userId] = {
                                totalShops: 0,
                                shops: [],
                                totalUsageGB: 0,
                                totalLimitGB: 0
                            };
                        }

                        storageMap[userId].totalShops++;
                        storageMap[userId].shops.push(shop);
                        
                        // Aggregate total usage and limit in GB
                        storageMap[userId].totalUsageGB += shop.totalUsageGB || 0;
                        storageMap[userId].totalLimitGB += shop.totalLimitGB || 0;
                    }
                });

                setStorageData(storageMap);
            }
        } catch (error) {
            console.error('Error fetching storage data:', error);
        }
    }, []);

    // --- FETCH WALLETS ---
    const fetchWallets = useCallback(async (page = 1) => {
        setLoading(true);
        clearMessages();

        try {
            const params = new URLSearchParams({
                page: page.toString(),
                // <-- MODIFIED: Use limit from state -->
                limit: limit.toString()
            });

            // Add filters if set
            if (filters.isActive !== '') params.append('isActive', filters.isActive);
            if (filters.minBalance) params.append('minBalance', filters.minBalance);
            if (filters.planType) params.append('planType', filters.planType);

            // Backend search filters
            if (searchFilters.name.trim()) params.append('name', searchFilters.name.trim());
            if (searchFilters.phone.trim()) params.append('phone', searchFilters.phone.trim());
            if (searchFilters.shopName.trim()) params.append('shopName', searchFilters.shopName.trim());

            const response = await apiWithAuth.get(
                `/wallet/admin/all-wallets?${params.toString()}`
            );

            if (response.data.success) {
                setWallets(response.data.data.wallets || []);
                setPagination(response.data.data.pagination);
            }
            setLoading(false);
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch wallets';
            setError(errorMessage);
            setLoading(false);
            setWallets([]);
        }
        // <-- MODIFIED: Update dependency array -->
    }, [clearMessages, filters, limit, searchFilters]);

    const skipSearchDebounceRef = useRef(true);

    useEffect(() => {
        fetchWallets(pagination.currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, []);

    useEffect(() => {
        if (skipSearchDebounceRef.current) {
            skipSearchDebounceRef.current = false;
            return;
        }

        const timer = setTimeout(() => {
            fetchWallets(1);
        }, 400);

        return () => clearTimeout(timer);
    }, [searchFilters.name, searchFilters.phone, searchFilters.shopName, fetchWallets]);

    useEffect(() => {
        if (wallets.length > 0) {
            fetchStorageData();
        }
    }, [wallets, fetchStorageData]);

    // useEffect for auto-dismissing success message
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                setSuccess(null);
            }, 5000); // 5 seconds

            // Cleanup timeout if component unmounts or success changes
            return () => clearTimeout(timer);
        }
    }, [success]); // Re-run effect when success message changes

    // <-- ADDED: Handler for changing rows per page -->
    const handleLimitChange = (e) => {
        const newLimit = Number(e.target.value);
        setLimit(newLimit);
        fetchWallets(1); // Reset to page 1
    };

    // Apply filters
    const applyFilters = () => {
        fetchWallets(1); // Reset to page 1 when applying filters
    };

    const resetFilters = () => {
        skipSearchDebounceRef.current = true;
        setFilters({
            isActive: '',
            minBalance: '',
            planType: ''
        });
        setSearchFilters({ name: '', phone: '', shopName: '' });
        fetchWallets(1);
    };

    const hasActiveSearch = Boolean(
        searchFilters.name.trim() || searchFilters.phone.trim() || searchFilters.shopName.trim()
    );

    // --- HELPER FUNCTIONS ---
    // Moved formatCurrency up to be available for handleSubmit
    const formatCurrency = (value, currency = 'INR') => {
        return `₹${(value || 0).toFixed(2)}`;
    };


    // --- FORM HANDLERS ---
    const fetchCreditPlans = useCallback(async () => {
        setLoadingCreditPlans(true);
        try {
            const response = await apiWithAuth.get('/subscription/plans');
            const allPlans = Array.isArray(response.data) ? response.data : (response.data?.data || []);
            const plans = allPlans
                .filter((plan) => !['F_MONTHLY', 'F_YEARLY'].includes(plan.planType))
                .map((plan) => ({
                    planType: plan.planType,
                    period: plan.period,
                    price: plan.price,
                    dailyDeductionRate: resolvePlanDailyRate(plan)
                }))
                .sort((a, b) => a.price - b.price);
            setCreditPlans(plans);
        } catch (err) {
            console.error('Failed to fetch subscription plans for credits:', err);
            setCreditPlans([]);
            setError(err.response?.data?.message || 'Failed to load subscription plans');
        } finally {
            setLoadingCreditPlans(false);
        }
    }, []);

    const resetFormData = () => {
        setFormData({ amount: '', description: '', planType: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        clearMessages();

        const creditAmount = parseFloat(formData.amount);
        if (isNaN(creditAmount) || creditAmount <= 0) {
            setError('Please enter a valid positive amount.');
            return;
        }
        if (!formData.planType) {
            setError('Please select a subscription plan.');
            return;
        }

        setLoading(true);
        try {
            const response = await apiWithAuth.post('/wallet/admin/add-credits', {
                userId: selectedWallet.user.userId,
                amount: creditAmount,
                description: formData.description,
                planType: formData.planType
            });

            if (response.data.success) {
                setShowForm(false);
                setSelectedWallet(null);
                resetFormData();
                fetchWallets(pagination.currentPage);
                setSuccess(`Successfully added ${formatCurrency(creditAmount)} to ${selectedWallet.user?.fullName}.`);
            }
            setLoading(false);
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to add credits';
            setError(errorMessage);
            setLoading(false);
        }
    };

    const handleOpenModal = (wallet) => {
        setSelectedWallet(wallet);
        setFormData({
            amount: '',
            description: '',
            planType: wallet.activeSubscriptionPlan && !['F_MONTHLY', 'F_YEARLY'].includes(wallet.activeSubscriptionPlan)
                ? wallet.activeSubscriptionPlan
                : ''
        });
        clearMessages();
        setShowForm(true);
        fetchCreditPlans();
    };

    const selectedCreditPlan = creditPlans.find((plan) => plan.planType === formData.planType);
    const estimatedCreditDays = selectedCreditPlan && formData.amount
        ? Math.floor(Number(formData.amount) / selectedCreditPlan.dailyDeductionRate)
        : null;

    const handleCancelForm = () => {
        setShowForm(false);
        setSelectedWallet(null);
        resetFormData();
        clearMessages();
    };

    const handleViewTransactions = (wallet) => {
        setSelectedTransactions(wallet);
        setShowTransactions(true);
    };

    const handleCloseTransactions = () => {
        setShowTransactions(false);
        setSelectedTransactions(null);
    };

    const handleViewStorage = (wallet) => {
        const userId = wallet.user?.userId;
        const userStorageData = storageData[userId];
        setSelectedStorage({ wallet, storageData: userStorageData });
        setShowStorage(true);
    };

    const handleCloseStorage = () => {
        setShowStorage(false);
        setSelectedStorage(null);
    };

    // --- PAGINATION HANDLERS ---
    const goToPage = (page) => {
        if (page >= 1 && page <= pagination.totalPages) {
            fetchWallets(page);
        }
    };

    const getShopDisplay = (wallet) => {
        if (wallet.shop?.name) {
            return wallet.shop.name;
        }
        const userShops = storageData[wallet.user?.userId]?.shops;
        if (!userShops || userShops.length === 0) return null;
        const first = userShops[0].shopName || 'Unnamed';
        if (userShops.length === 1) return first;
        return { first, extraCount: userShops.length - 1 };
    };

    // --- HELPER FUNCTIONS ---
    // formatCurrency moved up

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const getStatusBadge = (isExpired, daysRemaining, isActive) => {
        if (!isActive) {
            return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Inactive</span>;
        } else if (isExpired) {
            return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Expired</span>;
        } else if (daysRemaining <= 7) {
            return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Expiring Soon</span>;
        } else {
            return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Active</span>;
        }
    };

    const getTransactionTypeBadge = (type) => {
        const badges = {
            'CREDIT': 'bg-green-100 text-green-800',
            'DEBIT': 'bg-red-100 text-red-800',
            'INITIAL_GRANT': 'bg-blue-100 text-blue-800',
            'DAILY_DEDUCTION': 'bg-orange-100 text-orange-800',
            'SUBSCRIPTION_PURCHASE': 'bg-purple-100 text-purple-800',
            'MANUAL_ADJUSTMENT': 'bg-gray-100 text-gray-800'
        };
        const className = badges[type] || 'bg-gray-100 text-gray-800';
        return <span className={`px-2 py-1 text-xs font-semibold rounded ${className}`}>{type.replace('_', ' ')}</span>;
    };

    const getSubscriptionPlanBadgeColor = (plan) => {
        if (!plan) return 'bg-gray-100 text-gray-800';
        const colors = {
            'F_MONTHLY': 'bg-blue-100 text-blue-800',
            'F_YEARLY': 'bg-blue-100 text-blue-800',
            'B_MONTHLY': 'bg-[#cd7f32]/20 text-[#cd7f32]',
            'B_YEARLY': 'bg-[#cd7f32]/20 text-[#cd7f32]',
            'S_MONTHLY': 'bg-[#c0c0c0]/40 text-[#4b5563]',
            'S_YEARLY': 'bg-[#c0c0c0]/40 text-[#4b5563]',
            'G_MONTHLY': 'bg-[#fbbf24]/30 text-[#b45309]',
            'G_YEARLY': 'bg-[#fbbf24]/30 text-[#b45309]'
        };
        return colors[plan] || 'bg-gray-100 text-gray-800';
    };

    const getStorageUsageColor = (percentage) => {
        if (percentage >= 90) return 'text-red-600';
        if (percentage >= 70) return 'text-yellow-600';
        return 'text-green-600';
    };

    // --- JSX ---

    // <-- ADDED: Calculations for new pagination text -->
    const itemsStart = pagination.totalWallets > 0 ? ((pagination.currentPage - 1) * limit) + 1 : 0;
    const itemsEnd = Math.min(pagination.currentPage * limit, pagination.totalWallets);

    return (
        <div className="bg-gray-100 min-h-screen p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">User Wallet Management</h1>
                        <p className="mt-1 text-gray-600">View balances and manage user wallets</p>
                    </div>
                    <div className="mt-4 sm:mt-0 flex items-center gap-2">
                        <div className="text-sm text-gray-600">
                            Total: <span className="font-semibold">{pagination.totalWallets}</span> wallets
                        </div>
                    </div>
                </header>

                {/* Search and Filter Bar */}
                <div className="mb-6 space-y-4">
                    <div className="flex flex-col lg:flex-row gap-4">
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <SearchIcon />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Name"
                                    value={searchFilters.name}
                                    onChange={(e) => setSearchFilters({ ...searchFilters, name: e.target.value })}
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                            </div>
                            <input
                                type="text"
                                placeholder="Phone"
                                value={searchFilters.phone}
                                onChange={(e) => setSearchFilters({ ...searchFilters, phone: e.target.value })}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            />
                            <input
                                type="text"
                                placeholder="Shop name"
                                value={searchFilters.shopName}
                                onChange={(e) => setSearchFilters({ ...searchFilters, shopName: e.target.value })}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            />
                        </div>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="flex items-center justify-center px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors shrink-0"
                        >
                            <FilterIcon />
                            Filters
                        </button>
                    </div>

                    {/* Filters Panel */}
                    {showFilters && (
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                        value={filters.isActive}
                                        onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="">All</option>
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Min Balance</label>
                                    <input
                                        type="number"
                                        value={filters.minBalance}
                                        onChange={(e) => setFilters({ ...filters, minBalance: e.target.value })}
                                        placeholder="e.g., 1000"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Plan Type</label>
                                    <select
                                        value={filters.planType}
                                        onChange={(e) => setFilters({ ...filters, planType: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="">All Plans</option>
                                        <option value="F_MONTHLY">Free Monthly</option>
                                        <option value="B_MONTHLY">Basic Monthly</option>
                                        <option value="S_MONTHLY">Standard Monthly</option>
                                        <option value="G_MONTHLY">Gold Monthly</option>
                                        <option value="F_YEARLY">Free Yearly</option>
                                        <option value="B_YEARLY">Basic Yearly</option>
                                        <option value="S_YEARLY">Standard Yearly</option>
                                        <option value="G_YEARLY">Gold Yearly</option>
                                    </select>
                                </div>
                            </div>
                            <div className="mt-4 flex gap-2">
                                <button
                                    onClick={applyFilters}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                                >
                                    Apply Filters
                                </button>
                                <button
                                    onClick={resetFilters}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Success Message with timer bar */}
                {success && !showForm && (
                    <div className="relative bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 overflow-hidden" role="alert">
                        {/* Keyframes for the timer bar */}
                        <style>
                            {`
                          @keyframes width-shrink {
                            from { width: 100%; }
                            to { width: 0%; }
                          }
                          .animate-width-shrink {
                            animation: width-shrink 5s linear forwards;
                          }
                        `}
                        </style>
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="font-bold">Success</p>
                                <p>{success}</p>
                            </div>
                            <button
                                onClick={() => setSuccess(null)}
                                className="p-1 rounded-full text-green-600 hover:bg-green-200 hover:text-green-800"
                                aria-label="Close"
                            >
                                <CloseIcon />
                            </button>
                        </div>
                        {/* The timer bar line */}
                        <div className="absolute bottom-0 left-0 h-1 bg-green-400 animate-width-shrink"></div>
                    </div>
                )}

                {error && !showForm && (
                    <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
                        <p className="font-bold">Error</p>
                        <p>{error}</p>
                    </div>
                )}

                {/* Wallets Table */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    {loading ? (
                        <div className="text-center py-12 text-gray-500">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                            <p className="mt-2">Loading wallets...</p>
                        </div>
                    ) : wallets.length === 0 ? (
                        <div className="text-center py-12">
                            <h3 className="text-lg font-medium text-gray-900">No Wallets Found</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {hasActiveSearch
                                    ? 'No wallets match your search filters. Try different name, phone, or shop name values.'
                                    : 'No user wallets exist yet.'}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop Name</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Storage Usage</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transactions</th>
                                            <th scope="col" className="relative px-6 py-3">
                                                <span className="sr-only">Actions</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {wallets.map((wallet) => (
                                            <tr key={wallet.walletId} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {wallet.user?.fullName || 'N/A'}
                                                            </div>
                                                            <div className="text-sm text-gray-500">
                                                                {wallet.user?.email || 'N/A'}
                                                            </div>
                                                            {wallet.user?.phone && (
                                                                <div className="text-xs text-gray-400">
                                                                    {wallet.user.phone}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        {(() => {
                                                            const shopDisplay = getShopDisplay(wallet);
                                                            if (!shopDisplay) return <span className="text-gray-400">—</span>;
                                                            if (typeof shopDisplay === 'string') return shopDisplay;
                                                            return (
                                                                <span>
                                                                    {shopDisplay.first}{' '}
                                                                    <span className="text-gray-500">(+{shopDisplay.extraCount})</span>
                                                                </span>
                                                            );
                                                        })()}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900 font-semibold">
                                                        {formatCurrency(wallet.balance, wallet.currency)}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        Rate: {formatCurrency(wallet.dailyDeductionRate)}/day
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 py-1 text-xs font-medium rounded ${getSubscriptionPlanBadgeColor(wallet.activeSubscriptionPlan)}`}>
                                                        {wallet.activeSubscriptionPlan || 'Free'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        {formatDate(wallet.creditsExpiryDate)}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {wallet.daysRemaining > 0 ? `${wallet.daysRemaining} days left` : 'Expired'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        {getStatusBadge(wallet.isExpired, wallet.daysRemaining, wallet.isActive)}
                                                        <button
                                                            onClick={() => handleToggleWalletActive(wallet.user?.userId, wallet.isActive)}
                                                            disabled={togglingWallets.has(wallet.user?.userId)}
                                                            className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded transition-colors ${wallet.isActive
                                                                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                                                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                                                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                            title={wallet.isActive ? 'Deactivate Wallet' : 'Activate Wallet'}
                                                        >
                                                            {togglingWallets.has(wallet.user?.userId) ? (
                                                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                                                            ) : (
                                                                <>
                                                                    <ToggleIcon isActive={wallet.isActive} />

                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                    {(() => {
                                                        const userId = wallet.user?.userId;
                                                        const userStorage = storageData[userId];

                                                        if (!userStorage || !userStorage.shops || userStorage.shops.length === 0) {
                                                            return (
                                                                <div className="text-center">
                                                                    <span className="text-gray-400">No shops</span>
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <button
                                                                onClick={() => handleViewStorage(wallet)}
                                                                className="inline-flex items-center text-gray-600 hover:text-indigo-600 transition-colors"
                                                                title="View storage details"
                                                            >
                                                                <StorageIcon />
                                                                <div className="ml-2 text-left">
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {userStorage.totalUsageGB} GB
                                                                    </div>
                                                                    <div className="text-xs text-gray-400">
                                                                        {userStorage.totalShops} shops
                                                                    </div>
                                                                </div>
                                                            </button>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-center">
                                                    <button
                                                        onClick={() => handleViewTransactions(wallet)}
                                                        className="inline-flex items-center text-gray-600 hover:text-indigo-600 transition-colors"
                                                        title="View transactions"
                                                    >
                                                        <EyeIcon />
                                                        <span className="ml-1">{wallet.transactionCount || 0}</span>
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <button
                                                        onClick={() => handleOpenModal(wallet)}
                                                        className="inline-flex items-center text-indigo-600 hover:text-indigo-900 p-2 rounded-md hover:bg-indigo-50 transition-colors"
                                                    >
                                                        <WalletIcon /> Add Credits
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* <-- MODIFIED: New Pagination UI --> */}
                            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">

                                {/* Left Side: Rows per page */}
                                <div className="flex items-center">
                                    <label htmlFor="rows-per-page" className="text-sm text-gray-700 mr-2">
                                        Rows per page:
                                    </label>
                                    <select
                                        id="rows-per-page"
                                        name="rows-per-page"
                                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                        value={limit}
                                        onChange={handleLimitChange}
                                    >
                                        <option>10</option>
                                        <option>20</option>
                                        <option>50</option>
                                        <option>100</option>
                                    </select>
                                </div>

                                {/* Middle: Showing results */}
                                <div className="flex-1 flex justify-center">
                                    <p className="text-sm text-gray-700">
                                        Showing <span className="font-medium">{itemsStart}</span> to <span className="font-medium">{itemsEnd}</span> of{' '}
                                        <span className="font-medium">{pagination.totalWallets}</span> results
                                    </p>
                                </div>

                                {/* Right Side: Page controls */}
                                <div className="flex items-center">
                                    <button
                                        onClick={() => goToPage(pagination.currentPage - 1)}
                                        disabled={!pagination.hasPrevPage}
                                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-sm text-gray-700 mx-4">
                                        Page <span className="font-medium">{pagination.currentPage}</span> of <span className="font-medium">{pagination.totalPages}</span>
                                    </span>
                                    <button
                                        onClick={() => goToPage(pagination.currentPage + 1)}
                                        disabled={!pagination.hasNextPage}
                                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Add Credits Modal */}
            <div className={`fixed inset-0 bg-black bg-opacity-60 z-40 transition-opacity duration-300 ${showForm ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}></div>
            <div className={`fixed inset-0 z-50 flex justify-center items-center p-4 transition-all duration-300 ${showForm ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
                    <header className="flex items-center justify-between p-4 border-b">
                        <h2 className="text-xl font-semibold text-gray-800">Add Credits</h2>
                        <button onClick={handleCancelForm} className="p-1 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600">
                            <CloseIcon />
                        </button>
                    </header>
                    <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                        <div className="p-6 overflow-y-auto space-y-6">
                            {error && (
                                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
                                    <p>{error}</p>
                                </div>
                            )}

                            {selectedWallet && (
                                <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                                    <div className="text-sm font-medium text-gray-700">
                                        User: <span className="font-bold text-gray-900">{selectedWallet.user?.fullName}</span>
                                    </div>
                                    <div className="text-sm text-gray-500">{selectedWallet.user?.email}</div>
                                    <div className="mt-2 text-sm font-medium text-gray-700">
                                        Current Balance: <span className="font-bold text-gray-900">{formatCurrency(selectedWallet.balance)}</span>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        Days Remaining: {selectedWallet.daysRemaining} days
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Plan</label>
                                <select
                                    value={formData.planType}
                                    onChange={(e) => setFormData({ ...formData, planType: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    required
                                    disabled={loadingCreditPlans}
                                >
                                    <option value="">
                                        {loadingCreditPlans ? 'Loading plans...' : 'Select plan'}
                                    </option>
                                    {creditPlans.map((plan) => (
                                        <option key={plan.planType} value={plan.planType}>
                                            {PLAN_TYPE_LABELS[plan.planType] || plan.planType} — {Number(plan.dailyDeductionRate).toFixed(2)} credits/day
                                        </option>
                                    ))}
                                </select>
                                {!loadingCreditPlans && creditPlans.length === 0 && (
                                    <p className="text-xs text-red-600 mt-1">No plans available. Check subscription plans in admin.</p>
                                )}
                                {selectedCreditPlan && (
                                    <p className="text-xs text-gray-600 mt-1">
                                        Rate: <span className="font-medium">{Number(selectedCreditPlan.dailyDeductionRate).toFixed(2)} credits/day</span>
                                        {estimatedCreditDays != null && estimatedCreditDays > 0 && (
                                            <span> · ~{estimatedCreditDays} day(s) for entered amount</span>
                                        )}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                                <input
                                    type="number"
                                    value={formData.amount}
                                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="e.g., 500"
                                    step="0.01"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows="3"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
                                    placeholder="e.g., Manual bonus credits"
                                    required
                                ></textarea>
                            </div>
                        </div>
                        <footer className="flex gap-3 justify-end p-4 bg-gray-50 border-t mt-auto">
                            <button
                                type="button"
                                onClick={handleCancelForm}
                                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50"
                            >
                                {loading ? 'Adding...' : 'Add Credits'}
                            </button>
                        </footer>
                    </form>
                </div>
            </div>

            {/* Transactions Modal */}
            <div className={`fixed inset-0 bg-black bg-opacity-60 z-40 transition-opacity duration-300 ${showTransactions ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}></div>
            <div className={`fixed inset-0 z-50 flex justify-center items-center p-4 transition-all duration-300 ${showTransactions ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                    <header className="flex items-center justify-between p-4 border-b">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-800">Transaction History</h2>
                            {selectedTransactions && (
                                <p className="text-sm text-gray-600 mt-1">
                                    {selectedTransactions.user?.fullName || 'Unknown User'} - {selectedTransactions.user?.email}
                                </p>
                            )}
                        </div>
                        <button onClick={handleCloseTransactions} className="p-1 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600">
                            <CloseIcon />
                        </button>
                    </header>
                    <div className="flex-1 overflow-y-auto p-6">
                        {selectedTransactions && selectedTransactions.recentTransactions && selectedTransactions.recentTransactions.length > 0 ? (
                            <div className="space-y-4">
                                {/* Summary Cards */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                        <div className="text-sm text-blue-600 font-medium">Current Balance</div>
                                        <div className="text-2xl font-bold text-blue-900">{formatCurrency(selectedTransactions.balance)}</div>
                                    </div>
                                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                                        <div className="text-sm text-purple-600 font-medium">Total Transactions</div>
                                        <div className="text-2xl font-bold text-purple-900">{selectedTransactions.transactionCount}</div>
                                    </div>
                                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                        <div className="text-sm text-green-600 font-medium">Days Remaining</div>
                                        <div className="text-2xl font-bold text-green-900">{selectedTransactions.daysRemaining}</div>
                                    </div>
                                </div>

                                {/* Transactions List */}
                                <div className="space-y-3">
                                    {selectedTransactions.recentTransactions.map((transaction, index) => (
                                        <div key={transaction._id || index} className="bg-gray-50 p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        {getTransactionTypeBadge(transaction.type)}
                                                        <span className={`font-semibold ${transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                            {transaction.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(transaction.amount))}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-gray-700 mb-1">{transaction.description}</p>
                                                    <div className="flex items-center gap-4 text-xs text-gray-500">
                                                        <span>Balance After: {formatCurrency(transaction.balanceAfter)}</span>
                                                        <span>•</span>
                                                        <span>{formatDate(transaction.timestamp)}</span>
                                                        <span>{new Date(transaction.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                                                    </div>
                                                    {transaction.subscriptionPlan && (
                                                        <div className="mt-2">
                                                            <span className={`text-xs px-2 py-1 rounded ${getSubscriptionPlanBadgeColor(transaction.subscriptionPlan)}`}>
                                                                Plan: {transaction.subscriptionPlan}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {selectedTransactions.transactionCount > selectedTransactions.recentTransactions.length && (
                                    <div className="text-center py-4 text-sm text-gray-500">
                                        Showing {selectedTransactions.recentTransactions.length} of {selectedTransactions.transactionCount} transactions
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <p className="text-gray-500">No transactions found</p>
                            </div>
                        )}
                    </div>
                    <footer className="p-4 bg-gray-50 border-t">
                        <button
                            onClick={handleCloseTransactions}
                            className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                        >
                            Close
                        </button>
                    </footer>
                </div>
            </div>

            {/* Storage Usage Modal */}
            <div className={`fixed inset-0 bg-black bg-opacity-60 z-40 transition-opacity duration-300 ${showStorage ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}></div>
            <div className={`fixed inset-0 z-50 flex justify-center items-center p-4 transition-all duration-300 ${showStorage ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                    <header className="flex items-center justify-between p-4 border-b">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-800">Storage Usage Details</h2>
                            {selectedStorage && (
                                <p className="text-sm text-gray-600 mt-1">
                                    {selectedStorage.wallet.user?.fullName || 'Unknown User'} - {selectedStorage.wallet.user?.email}
                                </p>
                            )}
                        </div>
                        <button onClick={handleCloseStorage} className="p-1 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600">
                            <CloseIcon />
                        </button>
                    </header>
                    <div className="flex-1 overflow-y-auto p-6">
                        {selectedStorage && selectedStorage.storageData ? (
                            <div className="space-y-6">
                                {/* Overall Usage Summary */}
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Overall Storage Usage</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="bg-white p-4 rounded-lg shadow-sm">
                                            <div className="text-sm font-medium text-gray-600 mb-2">Total Usage</div>
                                            <div className="text-2xl font-bold text-gray-900">
                                                {selectedStorage.storageData.totalUsageGB} GB
                                            </div>
                                        </div>
                                        <div className="bg-white p-4 rounded-lg shadow-sm">
                                            <div className="text-sm font-medium text-gray-600 mb-2">Total Limit</div>
                                            <div className="text-2xl font-bold text-gray-900">
                                                {selectedStorage.storageData.totalLimitGB} GB
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-gray-600">Pending Limit</span>
                                            <span className="text-sm font-bold text-gray-900">
                                                {(selectedStorage.storageData.totalLimitGB - selectedStorage.storageData.totalUsageGB)} GB
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-indigo-600 h-2 rounded-full"
                                                style={{ width: `${Math.min((selectedStorage.storageData.totalUsageGB / selectedStorage.storageData.totalLimitGB) * 100 || 0, 100)}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Individual Shops */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                        Shop Storage Details ({selectedStorage.storageData.totalShops} shops)
                                    </h3>
                                    <div className="space-y-4">
                                        {selectedStorage.storageData.shops?.map((shop, index) => (
                                            <div key={shop.shopId || index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-3">
                                                        <h4 className="text-md font-semibold text-gray-800">{shop.shopName}</h4>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    {/* Images */}
                                                    <div className="bg-blue-50 p-3 rounded-lg">
                                                        <div className="text-sm font-medium text-blue-800 mb-2">Images</div>
                                                        <div className="text-sm font-bold text-blue-900">
                                                            {shop.imageUsageMB} MB
                                                        </div>
                                                    </div>

                                                    {/* Videos */}
                                                    <div className="bg-purple-50 p-3 rounded-lg">
                                                        <div className="text-sm font-medium text-purple-800 mb-2">Videos</div>
                                                        <div className="text-sm font-bold text-purple-900">
                                                            {shop.videoUsageMB} MB
                                                        </div>
                                                    </div>

                                                    {/* Products */}
                                                    <div className="bg-green-50 p-3 rounded-lg">
                                                        <div className="text-sm font-medium text-green-800 mb-2">Products</div>
                                                        <div className="text-sm font-bold text-green-900">
                                                            {shop.productUsageMB} MB
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mt-3 pt-3 border-t border-gray-200">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-medium text-gray-600">Total Usage</span>
                                                        <span className="text-sm font-bold text-gray-900">
                                                            {shop.totalUsageGB} GB
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-1">
                                                        <span className="text-sm font-medium text-gray-600">Limit</span>
                                                        <span className="text-sm font-bold text-gray-900">
                                                            {shop.totalLimitGB} GB
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <StorageIcon />
                                <h3 className="text-lg font-medium text-gray-900 mt-4">No Storage Data Available</h3>
                                <p className="text-gray-500 mt-2">This user doesn't have any shops or storage information.</p>
                            </div>
                        )}
                    </div>
                    <footer className="p-4 bg-gray-50 border-t">
                        <button
                            onClick={handleCloseStorage}
                            className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                        >
                            Close
                        </button>
                    </footer>
                </div>
            </div>
        </div>
    );
};

export default AdminWalletDashboard;